import React, { useState, useEffect } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import './HoursOfOperation.css';

const HoursOfOperation = () => {
  const [hours, setHours] = useState([
    { day: 'Monday', hours: 'Closed' },
    { day: 'Tuesday', hours: 'Closed' },
    { day: 'Wednesday', hours: 'Closed' },
    { day: 'Thursday', hours: 'Closed' },
    { day: 'Friday', hours: 'Closed' },
    { day: 'Saturday', hours: 'Closed' },
    { day: 'Sunday', hours: 'Closed' }
  ]);
  
  const [specialHoursNotes, setSpecialHoursNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Replace with your Google Maps Place ID for Burger Barn
  // You can find your place ID at: https://developers.google.com/maps/documentation/places/web-service/place-id
  const placeId = 'YOUR_PLACE_ID_HERE';
  
  // Load the Google Maps script using the official React package
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places']
  });
  
  useEffect(() => {
    const fetchHoursFromGoogleMaps = async () => {
      // Only proceed if the Google Maps script is loaded and we have a valid place ID
      if (isLoaded && placeId !== 'YOUR_PLACE_ID_HERE') {
        try {
          setLoading(true);
          
          // Create a Places service instance
          const service = new window.google.maps.places.PlacesService(document.createElement('div'));
          
          // Request details for the place
          service.getDetails(
            { 
              placeId: placeId,
              fields: ['opening_hours', 'business_status']
            },
            (place, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && place.opening_hours) {
                // Parse the opening hours
                const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const newHours = daysOfWeek.map((day, index) => {
                  const dayPeriods = place.opening_hours.periods?.filter(
                    period => period.open.day === index
                  );
                  
                  if (!dayPeriods || dayPeriods.length === 0) {
                    return { day, hours: 'Closed' };
                  }
                  
                  const formattedHours = dayPeriods.map(period => {
                    const openTime = formatTime(period.open.time);
                    const closeTime = formatTime(period.close.time);
                    return `${openTime} - ${closeTime}`;
                  }).join(', ');
                  
                  return { day, hours: formattedHours };
                });
                
                // Extract any special notes about hours or holidays
                let specialNotes = [];
                
                // Check if there are special day-specific texts (often contains holiday hours)
                if (place.opening_hours.weekday_text) {
                  const specialTexts = place.opening_hours.weekday_text.filter(text => {
                    // Look for common patterns that indicate special hours
                    return text.includes('holiday') || 
                           text.includes('special') || 
                           text.includes('exception') ||
                           text.includes('closed') ||
                           (text.includes(':') && !daysOfWeek.some(day => text.startsWith(day)));
                  });
                  
                  specialNotes = [...specialNotes, ...specialTexts];
                }
                
                // Check for additional special hour hints
                if (place.opening_hours.special_days) {
                  specialNotes.push(...place.opening_hours.special_days);
                }
                
                setHours(newHours.map(hourItem => {
                  // Find any special notes that might apply to this day
                  const daySpecificNotes = specialNotes.filter(note => 
                    note.toLowerCase().includes(hourItem.day.toLowerCase())
                  );
                  
                  if (daySpecificNotes.length > 0) {
                    return {
                      ...hourItem,
                      specialNote: daySpecificNotes.join('. ')
                    };
                  }
                  
                  return hourItem;
                }));
                
                // Store any general special notes that don't belong to a specific day
                if (specialNotes.length > 0) {
                  setSpecialHoursNotes(specialNotes.filter(note => 
                    !newHours.some(h => note.toLowerCase().includes(h.day.toLowerCase()))
                  ));
                }
              }
              setLoading(false);
            }
          );
        } catch (err) {
          console.error('Error fetching hours from Google Maps:', err);
          setError('Failed to load hours from Google Maps');
          setLoading(false);
        }
      }
    };
    
    // Format time from "0900" to "9:00 AM"
    const formatTime = (time) => {
      const hours = parseInt(time.substring(0, 2));
      const minutes = time.substring(2);
      const period = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      return `${formattedHours}:${minutes} ${period}`;
    };

    if (isLoaded && !loadError) {
      fetchHoursFromGoogleMaps();
    } else if (loadError) {
      setError('Error loading Google Maps API');
      setLoading(false);
    }
  }, [placeId, isLoaded, loadError]);

  return (
    <div className="hours-operation">
      <h2>Hours of Operation</h2>
      {loading ? (
        <p>Loading hours...</p>
      ) : error ? (
        <p>{error}</p>
      ) : (
        <>
          <div className="hours-grid">
            {hours.map((item) => (
              <React.Fragment key={item.day}>
                <div className="hours-day">{item.day}:</div>
                <div className="hours-time">
                  {item.hours}
                  {item.specialNote && (
                    <div className="hours-special-note">
                      <small>{item.specialNote}</small>
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
          
          {specialHoursNotes.length > 0 && (
            <div className="special-hours-notes">
              <h3>Special Hours & Notes</h3>
              <ul>
                {specialHoursNotes.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HoursOfOperation;